import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { apiErrorMsg } from "../../shared/services/api";

const AuthContext = createContext(null);

/**
 * v2.62 — Cookie-based auth (XSS hardening).
 *
 * Auth source of truth: the HTTPOnly `he_session` cookie set by the backend on
 * login/register/team-login. The frontend NEVER reads or stores the JWT; axios
 * is configured with `withCredentials: true` so the browser handles the cookie
 * automatically.
 *
 * Migration note: any pre-v2.62 user has `auth_token` in localStorage. The api
 * request interceptor still attaches it as a Bearer header for back-compat. On
 * the first successful `/auth/me` call after deploy, the backend opportunistically
 * installs the cookie — at which point we clear localStorage and the SPA stops
 * sending the header. Within one session refresh, every active user is migrated.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = unauth, undefined = loading
  const [loading, setLoading] = useState(true);
  const [presentationMode, setPresentationMode] = useState(false);

  const fetchMe = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      let merged = r.data;
      // v2.71.48 — When `/auth/me` resolves a team_member via the unified
      // users collection, the `sub_role` ("caller" | "agent") lives on the
      // separate `team_members` doc. Fetch it and merge so the persona
      // resolver (which looks at `user.sub_role`) routes field-agent users
      // to /team/field instead of falling back to caller.
      if (merged?.role === "team" || merged?.role === "team_member") {
        try {
          const r2 = await api.get("/team/me");
          merged = { ...merged, ...r2.data, role: merged.role };
        } catch { /* not a team_member or endpoint unavailable — ignore */ }
      }
      setUser(merged);
      try { localStorage.removeItem("auth_token"); } catch (_) { /* noop */ }
    } catch {
      // Maybe a team_member session — try team endpoint
      try {
        const r2 = await api.get("/team/me");
        setUser({ ...r2.data, role: "team_member" });
        try { localStorage.removeItem("auth_token"); } catch (_) { /* noop */ }
      } catch {
        try { localStorage.removeItem("auth_token"); } catch (_) { /* noop */ }
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Google OAuth callback: skip /auth/me here — AuthCallback exchanges the
    // session_id and establishes the session cookie first. Otherwise the /me
    // call races the cookie set and always returns 401.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // v2.62 — backend has set he_session cookie; do NOT persist token in localStorage.
    setUser(r.data.user);
    return r.data.user;
  };

  const register = async ({ email, password, name, role, phone }) => {
    const r = await api.post("/auth/register", { email, password, name, role, phone });
    setUser(r.data.user);
    return r.data.user;
  };

  const consumeMagic = async (token) => {
    const r = await api.post("/auth/magic-consume", { token });
    setUser(r.data.user);
    return r.data;
  };

  // v2.9 — team mobile OTP (legacy, kept for back-compat)
  const requestTeamOtp = async (mobile) => {
    const r = await api.post("/auth/team-otp/request", { mobile });
    return r.data; // { ok, demo, otp? }
  };

  const verifyTeamOtp = async (mobile, otp) => {
    const r = await api.post("/auth/team-otp/verify", { mobile, otp });
    setUser({ ...r.data.user, role: "team_member" });
    return r.data;
  };

  // v2.58 — One-step team login (no OTP). Used by /team-login.
  const teamLogin = async (mobile) => {
    const r = await api.post("/auth/team-login", { mobile });
    setUser({ ...r.data.user, role: "team_member" });
    return r.data;
  };

  const logout = async () => {
    // v2.62 — Tell the server to clear the HTTPOnly cookie. Best-effort: even if
    // the call fails (offline), we still drop client state.
    try { await api.post("/auth/logout"); } catch (_) { /* noop */ }
    try { localStorage.removeItem("auth_token"); } catch (_) { /* noop */ }
    setUser(null);
    if (typeof window !== "undefined") {
      window.location.replace("/");
    }
  };

  const refreshUser = fetchMe;

  const isAdmin = user?.role === "admin";
  const isDealer = user?.role === "dealer";
  const isBuilder = user?.role === "builder";
  // v2.19.2 — backend now returns role="team" for team_member tokens. Both labels recognised.
  const isTeam = user?.role === "team" || user?.role === "team_member";
  const isTeamMember = isTeam;
  const isClient = user?.role === "client";
  const tier = (isAdmin || isTeam) ? "premium" : (user?.subscription?.active ? user.subscription.tier : null);

  // v2.71.24 — Memoize the context value so consumers don't re-render every
  // time AuthProvider's parent re-renders. The deps array deliberately spreads
  // only the leaf values; derived flags (isAdmin etc.) are computed from
  // `user` so they're already stable when `user` is.
  const value = useMemo(
    () => ({
      user, loading, login, register, logout, refreshUser, consumeMagic,
      requestTeamOtp, verifyTeamOtp, teamLogin,
      isAdmin, isDealer, isBuilder, isTeam, isTeamMember, isClient, tier,
      presentationMode, setPresentationMode,
      apiErrorMsg,
    }),
    [user, loading, presentationMode, login, register, logout, refreshUser,
     consumeMagic, requestTeamOtp, verifyTeamOtp, teamLogin,
     isAdmin, isDealer, isBuilder, isTeam, isTeamMember, isClient, tier]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
